import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { Menu, MenuItem, MenuSeparator, MenuLabel } from '../ui/Menu';

const NAV = [
  {
    to: '/inspect',
    label: 'Inspect',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
    ),
  },
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
    ),
  },
];

interface TopBarProps {
  onOpenShare: () => void;
  onOpenWhatsApp: () => void;
  onOpenInspectors: () => void;
}

export function TopBar({ onOpenShare, onOpenWhatsApp, onOpenInspectors }: TopBarProps) {
  const { inspector, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const initials = (inspector?.display_name || '')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header
      className="sticky top-0 z-40 border-b border-[var(--card-border)] bg-[var(--card-bg)]"
      style={{ height: 'var(--topbar-h)' }}
    >
      <div className="mx-auto flex h-full max-w-[1280px] items-center gap-3 px-3 sm:px-5">
        {/* Brand */}
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--accent-color)]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-[var(--text-main)]">
              SKD Quality
            </div>
          </div>
        </div>

        {/* Primary nav — phones use the bottom tab bar instead */}
        <nav className="ml-4 hidden items-center gap-1 md:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-[var(--radius)] px-3 py-1.5 text-[0.8125rem] font-medium transition-colors ${
                  isActive
                    ? 'bg-[var(--accent-light)] text-[var(--accent-color)]'
                    : 'text-[var(--text-muted)] hover:bg-[var(--card-bg-subtle)] hover:text-[var(--text-main)]'
                }`
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1.5">
          <Menu
            label="Menu"
            trigger={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none" />
                <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
                <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" />
              </svg>
            }
          >
            {inspector && (
              <>
                <MenuLabel>Signed in as</MenuLabel>
                <div className="px-3 pb-2 text-[0.8125rem] font-medium text-[var(--text-main)]">
                  {inspector.display_name}
                  {inspector.is_admin && (
                    <span className="ml-1.5 text-[0.6875rem] font-normal text-[var(--text-dim)]">Admin</span>
                  )}
                </div>
                <MenuSeparator />
              </>
            )}

            <MenuItem
              onClick={onOpenShare}
              icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></svg>}
            >
              Share portal
            </MenuItem>
            <MenuItem
              onClick={onOpenWhatsApp}
              icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>}
            >
              Send WhatsApp report
            </MenuItem>

            <MenuSeparator />

            <MenuItem
              onClick={toggleTheme}
              icon={
                theme === 'dark' ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><line x1="12" y1="2" x2="12" y2="4" /><line x1="12" y1="20" x2="12" y2="22" /><line x1="4.9" y1="4.9" x2="6.3" y2="6.3" /><line x1="17.7" y1="17.7" x2="19.1" y2="19.1" /><line x1="2" y1="12" x2="4" y2="12" /><line x1="20" y1="12" x2="22" y2="12" /><line x1="4.9" y1="19.1" x2="6.3" y2="17.7" /><line x1="17.7" y1="6.3" x2="19.1" y2="4.9" /></svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
                )
              }
            >
              {theme === 'dark' ? 'Light theme' : 'Dark theme'}
            </MenuItem>

            {inspector?.is_admin && (
              <MenuItem
                onClick={onOpenInspectors}
                icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>}
              >
                Manage inspectors
              </MenuItem>
            )}

            <MenuSeparator />

            <MenuItem
              onClick={logout}
              danger
              icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>}
            >
              Sign out
            </MenuItem>
          </Menu>

          {inspector && (
            <div
              title={inspector.display_name}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--accent-light)] text-[0.6875rem] font-semibold text-[var(--accent-color)]"
            >
              {initials || '?'}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
